import {
  COST_TO_CURE_ISSUE_CATEGORY_TITLES,
  CostToCureReviewOutput,
} from '@aloft/toolkit-shared';
import { Block, Flex, Typography } from '@aloft/toolkit-sui';

import { ImageCarousel } from './ImageCarousel.tsx';

type CostToCureResultWithContext = {
  review: CostToCureReviewOutput;
  imageURLs?: string[];
  appraisal?: {
    costToCureAmount?: number;
    commentary?: string;
    conditionRating?: string;
  };
};

const formatDollars = (amount?: number) =>
  amount == null
    ? 'Not provided'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

export const CostToCureResults = ({ data }: { data: CostToCureResultWithContext }) => {
  const flags = data.review.status === 'success' ? data.review.output.flags : [];
  const hasFlags = flags.length > 0;
  const appraisal = data.appraisal ?? {};
  const imageURLs = data.imageURLs ?? [];

  return (
    <Flex flexDirection="column" gap="16px">
      <Block padding="16px" borderRadius="8px" backgroundColor="surface2">
        <Flex flexDirection="column" gap="12px">
          <Typography variant="h3">Provided Appraisal Context</Typography>
          <Flex gap="12px" flexWrap="wrap">
            <Block padding="10px 12px" borderRadius="6px" backgroundColor="surface1">
              <Typography variant="body2" color="textSecondary">
                Cost-to-Cure
              </Typography>
              <Typography variant="h4">{formatDollars(appraisal.costToCureAmount)}</Typography>
            </Block>
            <Block padding="10px 12px" borderRadius="6px" backgroundColor="surface1">
              <Typography variant="body2" color="textSecondary">
                Condition Rating
              </Typography>
              <Typography variant="h4">{appraisal.conditionRating ?? 'Not provided'}</Typography>
            </Block>
          </Flex>
          <Block padding="12px" borderRadius="6px" backgroundColor="surface1">
            <Typography variant="body2" color="textSecondary">
              Commentary
            </Typography>
            <Typography variant="body2">{appraisal.commentary ?? 'Not provided'}</Typography>
          </Block>
        </Flex>
      </Block>

      <Block padding="16px" borderRadius="8px" backgroundColor={hasFlags ? 'alertWarningLight' : 'surface2'}>
        <Flex flexDirection="column" gap="6px">
          <Typography variant="h3">
            {data.review.status === 'error'
              ? 'Review failed'
              : hasFlags
                ? `${flags.length} flag${flags.length === 1 ? '' : 's'}`
                : 'No flags'}
          </Typography>
          <Typography variant="body1">
            {data.review.status === 'success' ? data.review.output.summary : data.review.message}
          </Typography>
        </Flex>
      </Block>

      {hasFlags && (
        <Flex flexDirection="column" gap="12px">
          {flags.map((flag, index) => (
            <Block
              key={`${flag.category}-${index}`}
              padding="16px"
              borderRadius="8px"
              backgroundColor="surface1"
              border="1px solid #e0e0e0"
            >
              <Flex flexDirection="column" gap="8px">
                <Typography variant="h4">{COST_TO_CURE_ISSUE_CATEGORY_TITLES[flag.category]}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {flag.description}
                </Typography>
                {flag.photoUrls.length > 0 && (
                  <Flex flexDirection="column" gap="8px">
                    <Typography variant="body2" color="textSecondary">
                      Relevant photos
                    </Typography>
                    <ImageCarousel urls={flag.photoUrls} />
                  </Flex>
                )}
              </Flex>
            </Block>
          ))}
        </Flex>
      )}

      {imageURLs.length > 0 && (
        <Flex flexDirection="column" gap="8px">
          <Typography variant="h3">Report Photos</Typography>
          <ImageCarousel urls={imageURLs} />
        </Flex>
      )}
    </Flex>
  );
};
